import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  EyeIcon,
  CalendarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Sample attendance data
    const data = {
      overview: {
        presentDays: 22,
        totalWorkingDays: 23,
        attendancePercentage: 95.7,
        totalHours: 176,
        averageHoursPerDay: 8.0,
        lateArrivals: 2,
        earlyDepartures: 1,
        overtime: 4.5
      },
      todayStatus: {
        checkedIn: true,
        checkInTime: "09:15 AM",
        checkOutTime: null,
        workingHours: "7h 45m",
        status: "present",
        location: "Office - Tower A"
      },
      weeklyTrend: [
        { day: 'Mon', hours: 8.5, status: 'present' },
        { day: 'Tue', hours: 8.0, status: 'present' },
        { day: 'Wed', hours: 7.5, status: 'present' },
        { day: 'Thu', hours: 8.2, status: 'present' },
        { day: 'Fri', hours: 8.0, status: 'present' },
        { day: 'Sat', hours: 0, status: 'weekend' },
        { day: 'Sun', hours: 0, status: 'weekend' }
      ],
      leaveBalance: {
        annual: { used: 8, total: 21, remaining: 13 },
        sick: { used: 2, total: 12, remaining: 10 },
        casual: { used: 3, total: 7, remaining: 4 },
        maternity: { used: 0, total: 180, remaining: 180 }
      },
      upcomingLeaves: [
        {
          id: 1,
          type: "Annual Leave",
          startDate: "2025-08-05",
          endDate: "2025-08-07",
          days: 3,
          status: "approved",
          reason: "Family vacation"
        },
        {
          id: 2,
          type: "Casual Leave",
          startDate: "2025-08-15",
          endDate: "2025-08-15",
          days: 1,
          status: "pending",
          reason: "Personal work"
        }
      ]
    };

    const activity = [
      {
        id: 1,
        type: "check_in",
        time: "09:15 AM",
        date: "2025-07-23",
        description: "Checked in at Tower A",
        status: "on_time"
      },
      {
        id: 2,
        type: "check_out",
        time: "06:30 PM",
        date: "2025-07-22",
        description: "Checked out from Tower A",
        status: "overtime"
      },
      {
        id: 3,
        type: "leave_applied",
        time: "10:30 AM",
        date: "2025-07-22",
        description: "Applied for annual leave (Aug 5-7)",
        status: "approved"
      },
      {
        id: 4,
        type: "late_arrival",
        time: "09:45 AM",
        date: "2025-07-21",
        description: "Late arrival - Traffic delay",
        status: "late"
      }
    ];

    setTimeout(() => {
      setAttendanceData(data);
      setRecentActivity(activity);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
      case 'on_time':
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'late':
      case 'pending':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'absent':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading attendance dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-apollo-primary to-apollo-secondary text-white py-6 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <ChartBarIcon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 opacity-80" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Attendance Dashboard</h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Track your attendance, manage leave applications, and monitor your work hours
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Quick Actions */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/attendance/monthly')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2 text-xs sm:text-sm lg:text-base min-w-0"
          >
            <CalendarDaysIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="truncate">Monthly View</span>
          </button>
          <button
            onClick={() => navigate('/attendance/history')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2 text-xs sm:text-sm lg:text-base min-w-0"
          >
            <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="truncate">Attendance History</span>
          </button>
          <button
            onClick={() => navigate('/attendance/leave-balance')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2 text-xs sm:text-sm lg:text-base min-w-0"
          >
            <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="truncate">Leave Balance</span>
          </button>
        </div>

        {/* Today's Status */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 lg:mb-6">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center mb-2 sm:mb-0">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-apollo-accent flex-shrink-0" />
                <span className="truncate">Today's Status</span>
              </h2>
              <div className="text-xs sm:text-sm text-gray-500 w-full sm:w-auto text-left sm:text-right">
                <span className="hidden sm:inline">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <span className="sm:hidden">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              <div className="bg-green-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <CheckCircleSolidIcon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-green-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-medium text-green-900 truncate">Check In</div>
                    <div className="text-sm sm:text-base lg:text-lg font-bold text-green-800 truncate">{attendanceData.todayStatus?.checkInTime || '--'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-medium text-blue-900 truncate">Hours Worked</div>
                    <div className="text-sm sm:text-base lg:text-lg font-bold text-blue-800 truncate">{attendanceData.todayStatus?.workingHours || '--'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-medium text-purple-900 truncate">Location</div>
                    <div className="text-sm sm:text-base lg:text-lg font-bold text-purple-800 truncate">{attendanceData.todayStatus?.location || '--'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-orange-200">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {getStatusIcon(attendanceData.todayStatus?.status)}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-medium text-orange-900 truncate">Status</div>
                    <div className="text-sm sm:text-base lg:text-lg font-bold text-orange-800 capitalize truncate">
                      {attendanceData.todayStatus?.status || 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Stats */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Present Days</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{attendanceData.overview?.presentDays}</p>
                    <p className="text-xs text-gray-500 truncate">of {attendanceData.overview?.totalWorkingDays} days</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-green-100 rounded-full flex-shrink-0">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Attendance</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{attendanceData.overview?.attendancePercentage}%</p>
                    <div className="flex items-center text-xs text-green-600">
                      <ArrowUpIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">+2.3% from last month</span>
                    </div>
                  </div>
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-full flex-shrink-0">
                    <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Total Hours</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{attendanceData.overview?.totalHours}h</p>
                    <p className="text-xs text-gray-500 truncate">Avg: {attendanceData.overview?.averageHoursPerDay}h/day</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-purple-100 rounded-full flex-shrink-0">
                    <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Overtime</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{attendanceData.overview?.overtime}h</p>
                    <p className="text-xs text-gray-500 truncate">{attendanceData.overview?.lateArrivals} late arrivals</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-orange-100 rounded-full flex-shrink-0">
                    <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Trend */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">This Week's Attendance</h3>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {attendanceData.weeklyTrend?.map((day, index) => (
                  <div key={index} className="text-center">
                    <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate">{day.day}</div>
                    <div className={`w-full h-16 sm:h-20 lg:h-24 rounded-md sm:rounded-lg flex items-end justify-center p-1 sm:p-2 ${
                      day.status === 'present' ? 'bg-green-100 border border-green-200' :
                      day.status === 'weekend' ? 'bg-gray-100 border border-gray-200' :
                      'bg-red-100 border border-red-200'
                    }`}>
                      {day.hours > 0 && (
                        <div className="text-xs font-semibold text-gray-800">
                          {day.hours}h
                        </div>
                      )}
                    </div>
                    <div className="mt-1 sm:mt-2 flex justify-center">
                      {getStatusIcon(day.status)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Recent Activity</h3>
                <button
                  onClick={() => navigate('/attendance/history')}
                  className="text-apollo-accent hover:text-apollo-primary font-medium flex items-center space-x-1"
                >
                  <span>View All</span>
                  <EyeIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                    <div className="flex-shrink-0">
                      {getStatusIcon(activity.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">{activity.date} at {activity.time}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(activity.status)}`}>
                        {activity.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Leave Balance */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Leave Balance</h3>
                <button
                  onClick={() => navigate('/attendance/leave-balance')}
                  className="text-apollo-accent hover:text-apollo-primary font-medium text-sm"
                >
                  View Details
                </button>
              </div>
              <div className="space-y-4">
                {Object.entries(attendanceData.leaveBalance || {}).map(([type, balance]) => (
                  <div key={type} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 capitalize">{type} Leave</span>
                      <span className="text-sm text-gray-600">{balance.remaining}/{balance.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-apollo-accent h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(balance.remaining / balance.total) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {balance.used} used • {balance.remaining} remaining
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Leaves */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Upcoming Leaves</h3>
              <div className="space-y-4">
                {attendanceData.upcomingLeaves?.map((leave) => (
                  <div key={leave.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{leave.type}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(leave.status)}`}>
                        {leave.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {leave.days} day{leave.days > 1 ? 's' : ''}
                    </div>
                    {leave.reason && (
                      <div className="text-xs text-gray-500 italic">
                        {leave.reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-3 px-4 rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-medium">
                  Apply for Leave
                </button>
                <button 
                  onClick={() => navigate('/attendance/team')}
                  className="w-full border border-apollo-accent text-apollo-accent py-3 px-4 rounded-xl hover:bg-apollo-accent/5 transition-colors font-medium"
                >
                  Team Attendance
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors font-medium">
                  Download Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AttendanceDashboard;
